import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from "react"

import { useApi } from "@/hooks/UseApi"
import { useAuth } from "@/providers/AuthProvider"

import type { OnboardingStatus } from "@/services/onboarding.service"

interface OnboardingContextValue {
  status: OnboardingStatus | null
  loading: boolean
  refreshStatus: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi()
  const { authStatus } = useAuth()
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshStatus = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.onboarding.getOnboardingStatus()
      setStatus(data)
    } catch (error) {
      console.error("Failed to load onboarding status:", error)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (authStatus !== "authenticated") {
      return
    }
    refreshStatus()
  }, [authStatus, refreshStatus, api])

  const value = useMemo(
    () => ({ status, loading, refreshStatus }),
    [status, loading, refreshStatus]
  )

  return (
    <OnboardingContext.Provider value={ value }>
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within <OnboardingProvider></OnboardingProvider>')
  }
  return context
}
